import UaParser from 'ua-parser-js';

import { getFeatureConfig } from '@luna/feature-config';
import { logger } from '@luna/logger';
import { getCurrentLanguage } from '@luna/i18n';
import { selectMyProfileData } from '@shared/profile';

import { getSessionId, getVersion } from '../utils/TrackingService';
import { selectExternalIdentifiersData } from './ExternalIdentifiersQuery';

interface PostHogCrossApplicationTrackingProperties {
    sessionId: string;
    appName: string;
    appVersion: string;
    country: string;
    language: string;
    platform: string;
    osName?: string;
    osVersion?: string;
    deviceType: string;
    licenseId?: string;
    userId?: string;
    distinctId: string;
}

function getDistinctId(): string {
    const shouldUseUserNumberAsDistinctId = getFeatureConfig(
        (features) => !!features.tracking?.posthog?.shouldUseUserNumberAsDistinctId,
    );

    const profile = selectMyProfileData();
    if (shouldUseUserNumberAsDistinctId) {
        return profile?.userNumber ?? 'unknownUserId';
    }

    return profile?.customerId ?? 'unknownUserId';
}

function getUserId(): string | undefined {
    if (!getFeatureConfig((config) => config.tracking?.posthog?.shouldTrackUserId)) {
        return undefined;
    }

    return selectMyProfileData()?.userNumber?.toString();
}

function getLicenseId(): string | undefined {
    if (!getFeatureConfig((config) => config.tracking?.posthog?.shouldTrackGroups)) {
        return undefined;
    }

    return selectMyProfileData()?.companyInfo?.licenseHolder?.toString();
}

export function getPostHogCrossApplicationTrackingProperties():
    | PostHogCrossApplicationTrackingProperties
    | undefined {
    const appName = getFeatureConfig((config) => config.tracking?.appName);
    const country = getFeatureConfig((config) => config.base.country);

    if (!appName) {
        logger.debug('AppName configuration is missing');
        return undefined;
    }

    const uaParser = new UaParser();

    let distinctId = getDistinctId();

    const shouldUseExternalIdAsDistinctId = getFeatureConfig(
        (config) => config.tracking?.posthog?.shouldUseExternalIdAsDistinctId,
    );

    if (shouldUseExternalIdAsDistinctId) {
        const cachedIdentifier = selectExternalIdentifiersData();
        if (cachedIdentifier?.identifier) {
            distinctId = cachedIdentifier.identifier;
        }
    }

    return {
        sessionId: getSessionId(),
        appName,
        appVersion: getVersion(),
        country,
        language: getCurrentLanguage(),
        platform: 'web',
        osName: uaParser.getOS().name,
        osVersion: uaParser.getOS().version,
        // desktop is not part of ua string and won't be returned, lets use it as fallback
        // https://github.com/faisalman/ua-parser-js/issues/182
        deviceType: uaParser.getDevice().type ?? 'desktop',
        licenseId: getLicenseId(),
        userId: getUserId(),
        distinctId,
    };
}
